import { useState } from "react";
import { VENUE_LIST } from "../lib/data";
import { SeverityIcon } from "./SeverityIcon";
import type { IncidentEntry } from "../lib/types";

const SEVERITIES: {
  value: IncidentEntry["severity"];
  label: string;
  cls: string;
  pill: string;
}[] = [
  {
    value: "info",
    label: "Info",
    cls: "border-zinc-700",
    pill: "bg-zinc-700 text-zinc-200",
  },
  {
    value: "let-op",
    label: "Let op",
    cls: "border-warn/50",
    pill: "bg-warn/20 text-warn",
  },
  {
    value: "actie",
    label: "Actie",
    cls: "border-accent/50",
    pill: "bg-accent/20 text-accent",
  },
  {
    value: "alarm",
    label: "Alarm",
    cls: "border-risk/60",
    pill: "bg-risk/25 text-risk",
  },
];

const COMMON_LOCATIONS = [
  ...VENUE_LIST,
  "Centralist",
  "ECC",
  "Ingang A",
  "Ingang B",
  "Ingang C",
  "Ingang D",
  "Overig",
];

export interface QuickLogPrefill {
  location?: string;
  severity?: IncidentEntry["severity"];
  title?: string;
}

export function QuickLog({
  prefill,
  onSubmit,
  onCancel,
  compact,
  onOpenBommelding,
}: {
  prefill?: QuickLogPrefill;
  onSubmit: (i: Omit<IncidentEntry, "id">) => Promise<void>;
  onCancel: () => void;
  compact?: boolean;
  onOpenBommelding?: () => void;
}) {
  const [severity, setSeverity] = useState<IncidentEntry["severity"]>(
    prefill?.severity ?? "info",
  );
  const [location, setLocation] = useState<string>(
    prefill?.location ?? VENUE_LIST[0],
  );
  const [title, setTitle] = useState(prefill?.title ?? "");
  const [body, setBody] = useState("");

  const canSubmit = title.trim().length > 1;

  // If prefilled location isn't in COMMON_LOCATIONS, include it.
  const locationOptions = COMMON_LOCATIONS.includes(location)
    ? COMMON_LOCATIONS
    : [location, ...COMMON_LOCATIONS];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        void onSubmit({
          severity,
          location,
          title: title.trim(),
          body: body.trim() || undefined,
          time: Date.now(),
          resolved: false,
        });
      }}
      className={`card ${compact ? "p-2" : "p-3"} mb-2`}
    >
      {onOpenBommelding && (
        <button
          type="button"
          onClick={onOpenBommelding}
          className="w-full py-1.5 rounded-lg border border-risk/40 bg-risk/[0.08] text-risk text-xs mb-2 text-center hover:bg-risk/[0.14] transition-colors"
        >
          Bommelding-protocol
        </button>
      )}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {SEVERITIES.map((s) => (
          <button
            type="button"
            key={s.value}
            onClick={() => setSeverity(s.value)}
            aria-pressed={severity === s.value}
            className={`px-2.5 py-1.5 rounded-full text-2xs font-medium inline-flex items-center gap-1.5 min-h-[36px] transition-colors ${
              severity === s.value
                ? s.pill
                : "bg-ink-800 text-zinc-400 border border-line"
            }`}
          >
            <SeverityIcon severity={s.value} className="w-2.5 h-2.5" />
            {s.label}
          </button>
        ))}
      </div>
      <label htmlFor="ql-location" className="sr-only">
        Locatie
      </label>
      <select
        id="ql-location"
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        className="w-full bg-ink-900 border border-white/[0.08] rounded-lg px-3 py-2 text-sm mb-2 focus:border-accent/60 focus:outline-none"
      >
        {locationOptions.map((v) => (
          <option key={v}>{v}</option>
        ))}
      </select>
      <label htmlFor="ql-title" className="sr-only">
        Wat gebeurt er
      </label>
      <input
        id="ql-title"
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Wat gebeurt er?"
        required
        aria-required="true"
        aria-invalid={title.length > 0 && title.trim().length < 2}
        aria-describedby="ql-title-hint"
        className="w-full bg-ink-900 border border-white/[0.08] rounded-lg px-3 py-2 text-sm mb-1 focus:border-accent/60 focus:outline-none"
      />
      <p
        id="ql-title-hint"
        className={`text-2xs mb-2 ${
          title.length > 0 && title.trim().length < 2
            ? "text-warn"
            : "text-zinc-600"
        }`}
      >
        {title.length > 0 && title.trim().length < 2
          ? "Minimaal 2 tekens"
          : "Korte beschrijving — details kunnen in de toelichting"}
      </p>
      <label htmlFor="ql-body" className="sr-only">
        Toelichting
      </label>
      <textarea
        id="ql-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Toelichting (optioneel)"
        rows={2}
        className="w-full bg-ink-900 border border-white/[0.08] rounded-lg px-3 py-2 text-sm mb-3 resize-none focus:border-accent/60 focus:outline-none"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 rounded-lg border border-line text-sm text-zinc-400"
        >
          Annuleren
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="flex-1 py-2 rounded-lg bg-accent text-ink-950 font-semibold text-sm disabled:opacity-40"
        >
          Loggen
        </button>
      </div>
    </form>
  );
}
